import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "~/common/components/Table/TableElements";
import LabelBox from "~/domains/salesCampaign/components/LabelBox/LabelBox";
import { Link } from "~/common/components/Link";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { SortDirection, useSorting } from "~/common/hooks/useSorting";
import { type ComponentType, useState } from "react";
import type { UpdatedContractOverviewData } from "~/features/contracts/redux/types";
import { useTranslation } from "react-i18next";
import { Stack, styled, type SvgIconProps } from "~/common/mui";
import ActiveIcon from "~/common/mui/icons/Active";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WatchLaterIcon from "@mui/icons-material/WatchLater";

const StyledCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "dataCell",
})<{ dataCell?: "contractNumber" | "netPrice" }>({
  verticalAlign: "middle !important",
  "& > p": { margin: 0 },
  variants: [
    {
      props: { dataCell: "contractNumber" },
      style: { fontWeight: "bold !important" },
    },
    {
      props: { dataCell: "netPrice" },
      style: { paddingLeft: "10px !important" },
    },
  ],
});

interface NewContractTableProps {
  contractData: UpdatedContractOverviewData[];
}

export enum ContractSort {
  contractNumber = "contractNumber",
  contractPeriod = "contractPeriod",
  designation = "designation",
  schaefflerId = "schaefflerId",
  customerMaterialNumber = "customerMaterialNumber",
  totalPrice = "totalPrice",
  disposition = "disposition",
  status = "status",
}

export enum Status {
  // This ordering decides the "importance" of each status for the sorting
  Active,
  Waiting,
  Completed,
  Cancelled,
}

const STATUS_CODES_MAP = {
  "codes.contractStatus.01": Status.Waiting,
  "codes.contractStatus.02": Status.Active,
  "codes.contractStatus.05": Status.Completed,
  "codes.contractStatus.06": Status.Completed,
  "codes.contractStatus.07": Status.Cancelled,
};

const getStatusSortOrder = (status: string) => STATUS_CODES_MAP[status as keyof typeof STATUS_CODES_MAP] ?? Status.Cancelled;

const ICON_MAP: Record<Status, ComponentType<SvgIconProps>> = {
  [Status.Active]: ActiveIcon,
  [Status.Waiting]: WatchLaterIcon,
  [Status.Completed]: CheckIcon,
  [Status.Cancelled]: CloseIcon,
};

const sortDates = (firstDate?: Date, secondDate?: Date) => {
  const aDate = firstDate ? firstDate : null;
  const bDate = secondDate ? secondDate : null;

  if (aDate === null && bDate === null) {
    return 0;
  }
  if (aDate === null) {
    return 1;
  }
  if (bDate === null) {
    return -1;
  }
  return aDate.valueOf() - bDate.valueOf();
};

const SORT_HANDLERS: Dict<(a: UpdatedContractOverviewData, b: UpdatedContractOverviewData) => number> = {
  [ContractSort.contractNumber]: (a, b) => a.contractNumber.localeCompare(b.contractNumber),
  [ContractSort.contractPeriod]: (a, b) => {
    if (!a.startDate || !a.endDate) {
      return !b.startDate || !b.endDate ? 0 : 1;
    }
    if (!b.startDate || !b.endDate) {
      return -1;
    }
    return a.startDate === b.startDate ? 0 : a.startDate > b.startDate ? 1 : -1;
  },
  [ContractSort.designation]: (a, b) => a.designation.localeCompare(b.designation),
  [ContractSort.schaefflerId]: (a, b) => parseInt(a.materialNumber) - parseInt(b.materialNumber),
  [ContractSort.customerMaterialNumber]: (a, b) => parseInt(a.contractNumber) - parseInt(b.contractNumber),
  [ContractSort.totalPrice]: (a, b) => a.netPrice.value - b.netPrice.value,
  [ContractSort.disposition]: (a, b) => sortDates(a.disposition, b.disposition),
  [ContractSort.status]: (a, b) => {
    const statusComparison = getStatusSortOrder(a.status) - getStatusSortOrder(b.status);

    if (statusComparison === 0) {
      return sortDates(a.disposition, b.disposition);
    } else {
      return statusComparison;
    }
  },
};

const NewContractTable = ({ contractData }: NewContractTableProps) => {
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState(ContractSort.status);
  const [sortDirection, setSortDirection] = useState(SortDirection.Asc);

  const rows = useSorting(contractData, SORT_HANDLERS, sortCode, sortDirection);

  const createSortHandler = (codeToSet: ContractSort) => () => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: ContractSort) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const contractEntries = rows?.map((contract, index) => {
    const contractPeriod =
      contract.startDate &&
      contract.endDate &&
      `${formatDate(contract.startDate, DateFormatType.DATE)} - ${formatDate(contract.endDate, DateFormatType.DATE)}`;
    const disposition = contract.disposition && formatDate(contract.disposition, DateFormatType.DATE);
    const StatusIcon = ICON_MAP[getStatusSortOrder(contract.status)];
    return (
      <TableRow key={index}>
        <StyledCell dataCell="contractNumber">
          <p>{contract.contractNumber ?? "-"}</p>
        </StyledCell>
        <StyledCell>
          <p>{contractPeriod ?? "-"}</p>
        </StyledCell>
        <StyledCell>
          <p className={"f-no-wrap"}>{contract.designation ?? "-"}</p>
        </StyledCell>
        <StyledCell>
          <p>{contract.materialNumber ?? "-"}</p>
        </StyledCell>
        <StyledCell>
          <p>{contract.customerMaterialNumber ?? "-"}</p>
        </StyledCell>
        <StyledCell dataCell="netPrice">
          <p>{contract.netPrice.formattedValue}</p>
        </StyledCell>
        <StyledCell>
          <p>{disposition ?? "-"}</p>
        </StyledCell>
        <StyledCell>
          <LabelBox variant="darkGrey" sx={{ fontWeight: "normal", py: 0.25, px: 1 }}>
            <Stack gap={0.5} direction={"row"} sx={{ alignItems: "center", whiteSpace: "nowrap" }}>
              <StatusIcon className={"d-flex"}  />
              {t(contract.status) ?? "-"}
            </Stack>
          </LabelBox>
        </StyledCell>
        <StyledCell sx={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
          <Link href={contract.detailsUrl}>{t("contractOverview.table.details")}</Link>
        </StyledCell>
      </TableRow>
    );
  });
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.contractNumber)}>
              {t("contractOverview.table.contractNo")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.contractPeriod)}>
              {t("contractOverview.table.period")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.designation)}>
              {t("contractOverview.table.designation")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.schaefflerId)}>
              {t("productComparison.download.schaefflerId")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.customerMaterialNumber)}>
              {t("contractOverview.searchType.customerMaterialNumber")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              disabled
              hideSortIcon
              sx={{ fontWeight: "bold" }}
              {...createTableProps(ContractSort.totalPrice)}
            >
              {t("contractOverview.table.netPrice")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.disposition)}>
              {t("contractOverview.table.disposition")}
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel sx={{ fontWeight: "bold" }} {...createTableProps(ContractSort.status)}>
              {t("quoteDetailsPage.table.header.status")}
            </TableSortLabel>
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>

      <TableBody>{contractEntries}</TableBody>
    </Table>
  );
};

export default NewContractTable;
